import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, Button, Alert, CircularProgress, Paper, Divider, Fade, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { SECTION_COLORS } from "../constants";
import { useBrand } from "../../../contexts/BrandContext";
import { lookupLoyaltyCustomer, looksLikeEmail } from "../utils/customerLookup";

const CustomerLookupDialog = ({ open, onClose }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const [query, setQuery] = useState("");
    const [result, setResult] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const resetResults = () => {
        setResult(null);
        setCandidates([]);
        setError(null);
    };

    const handleSearch = async () => {
        if (!brandName || !query.trim()) return;
        setLoading(true);
        resetResults();
        try {
            const outcome = await lookupLoyaltyCustomer(brandName, query);
            if (outcome.kind === "single") {
                setResult(outcome.customer);
            } else {
                setCandidates(outcome.items || []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setQuery("");
        resetResults();
        onClose();
    };

    const openProfile = (profileId, email = "") => {
        if (!profileId) return;
        handleClose();
        const qs = email ? `?email=${encodeURIComponent(email)}` : "";
        navigate(`/fidelite/profil/${encodeURIComponent(profileId)}${qs}`);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.search.gradient }}>
                        <PersonSearchIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={600} color="#2c3e50">
                        Consulter un Client
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <TextField
                    fullWidth
                    label="Email ou Profile ID"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && query.trim() && brandName && handleSearch()}
                    placeholder="yes@gmail.com ou UUID…"
                    size="medium"
                    disabled={!brandName}
                    helperText="Recherche par email (recommandé après fusion Unomi) ou Profile ID Loyalty / Unomi"
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: SECTION_COLORS.search.main }} /></InputAdornment> }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2.5, fontSize: 14, fontFamily: "Montserrat, sans-serif",
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: SECTION_COLORS.search.main },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: SECTION_COLORS.search.main, borderWidth: 2 },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: SECTION_COLORS.search.main },
                    }}
                />

                {loading && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} sx={{ color: SECTION_COLORS.search.main }} /></Box>}
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}

                {candidates.length > 0 && (
                    <Box mt={2}>
                        <Typography fontSize={12} color="text.secondary" mb={1}>
                            {candidates.length} client(s) correspondant(s) — sélectionnez un profil :
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e7ff", borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "#f8f9ff" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Profile ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Niveau</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {candidates.map((row) => (
                                        <TableRow
                                            key={row.id || row.profile_id}
                                            hover
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => openProfile(row.profile_id, looksLikeEmail(query) ? query.trim() : "")}
                                        >
                                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{row.profile_id}</TableCell>
                                            <TableCell>{row.points_balance ?? 0}</TableCell>
                                            <TableCell>{row.loyalty_status_name || row.loyalty_status || "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {result && (
                    <Fade in>
                        <Paper elevation={0} sx={{ mt: 2, p: 2.5, borderRadius: 3, background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)", border: "1px solid #e0e7ff" }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <Avatar sx={{ bgcolor: SECTION_COLORS.search.main, width: 36, height: 36 }}>
                                    {result.profile_id?.charAt(0)?.toUpperCase() || "?"}
                                </Avatar>
                                <Box>
                                    <Typography fontWeight={700} fontSize={15} fontFamily="Montserrat, sans-serif">{result.profile_id}</Typography>
                                    <Typography fontSize={12} color="text.secondary">{result.brand}</Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <AccountBalanceWalletIcon sx={{ color: SECTION_COLORS.tiers.main, fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color={SECTION_COLORS.tiers.main}>{result.points_balance ?? 0}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Points Disponibles</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <EmojiEventsIcon sx={{ color: "#FFD93D", fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color="#1a1a2e">{result.loyalty_status_name || result.loyalty_status || "–"}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Niveau Fidélité</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <TrendingUpIcon sx={{ color: SECTION_COLORS.rewards.main, fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color={SECTION_COLORS.rewards.main}>{result.lifetime_points ?? 0}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Points à Vie</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box mt={3} display="flex" justifyContent="center">
                                <Button variant="outlined"
                                    onClick={() => openProfile(
                                        result.profile_id,
                                        looksLikeEmail(query) ? query.trim() : "",
                                    )}
                                    sx={{
                                        borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                                        borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                                    }}>
                                    Voir le Profil Fidélité
                                </Button>
                            </Box>
                        </Paper>
                    </Fade>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={handleSearch} disabled={!brandName || !query.trim() || loading}
                    startIcon={<SearchIcon />}
                    sx={{
                        background: SECTION_COLORS.search.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.search.main}40` },
                    }}>
                    Rechercher
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerLookupDialog;
